import classnames from 'classnames';
import {forwardRef, HTMLAttributes} from 'react';

import {Theme} from '@/common/contexts';
import {ComponentPlacementDirection, ComponentSizeXSToL} from '@/common/types';
import FontAwesomeV6Icon, {FontAwesomeV6IconProps} from '@/fontAwesomeV6Icon';

import moduleStyles from './tooltip.module.scss';

export interface TooltipProps extends HTMLAttributes<HTMLDivElement> {
  /** Tooltip text */
  text: string | React.ReactNode;
  /**
   * Tooltip id. Required to connect the element using the tooltip.
   * Also, you need to pass this id to that element as aria-describedBy html attribute
   * */
  tooltipId: string;
  /** Tooltip left icon */
  iconLeft?: FontAwesomeV6IconProps;
  /** Tooltip right icon */
  iconRight?: FontAwesomeV6IconProps;
  /** Tooltip direction/position relative to connected element */
  direction?: ComponentPlacementDirection;
  /** Tooltip custom className */
  className?: string;
  /** Tooltip size */
  size?: ComponentSizeXSToL;
  /** Tooltip custom styles (used for positioning the tooltip on the go) */
  style?: React.CSSProperties;
  /** Hides the tooltip's tail (arrow). Defaults to false. */
  hideTail?: boolean;
  /**
   *  Theme to set `data-theme` directly on the tooltip's outer div
   *  since `WithTooltip` renders the `Tooltip` in a portal and otherwise
   *  will not have `data-theme` set on an ancester element even when it
   *  is "logically" within a theme provider.
   **/
  'data-theme'?: Theme;
}

export interface TooltipOverlayProps {
  /** TooltipOverlay children (Element that needs a Tooltip + Tooltip itself) */
  children: React.ReactNode;
  /** TooltipOverlay custom className */
  className?: string;
}

/** TooltipOverlay component
 * Used to wrap the element that needs a Tooltip + Tooltip itself to make sure the Tooltip is displayed correctly
 * */
export const TooltipOverlay: React.FunctionComponent<TooltipOverlayProps> = ({
  children,
  className,
}) => (
  <div className={classnames(moduleStyles.tooltipOverlay, className)}>
    {children}
  </div>
);

/**
 * ## Production-ready Checklist:
 *  * (✔) implementation of component approved by design team;
 *  * (✔) has storybook, covered with stories and documentation;
 *  * (✔) has tests: test every prop, every state and every interaction that's js related;
 *  * (see ./__tests__/Tooltip.test.tsx)
 *  * (?) passes accessibility checks;
 *
 * ###  Status: ```Ready for dev```
 *
 * Design System: Tooltip Component.
 * Renders Tooltip for a given html element (Needs to be wrapped in TooltipOverlay, see stories or README.md).
 */
const Tooltip = forwardRef<HTMLDivElement, TooltipProps>(
  (
    {
      text,
      tooltipId,
      iconLeft,
      iconRight,
      direction = 'onTop',
      className,
      size = 'm',
      style = {},
      hideTail = false,
      ...HTMLAttributes
    },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        id={tooltipId}
        aria-live="polite"
        className={classnames(
          moduleStyles.tooltip,
          moduleStyles[`tooltip-${direction}`],
          moduleStyles[`tooltip-${size}`],
          hideTail && moduleStyles.noTail,
          className,
        )}
        style={style}
        role="tooltip"
        {...HTMLAttributes}
      >
        {iconLeft && <FontAwesomeV6Icon {...iconLeft} />}
        <span className={moduleStyles.tooltipText}>{text}</span>
        {iconRight && <FontAwesomeV6Icon {...iconRight} />}
      </div>
    );
  },
);

export default Tooltip;
